import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Button,
} from "@material-ui/core";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import { TextBox } from "../../atoms/Text Box/TextBox";
import {
  BigRedButton,
  ButtonVersion,
} from "../../atoms/BigRedButton/BigRedButtonAtom";
import useApiService from "../../utilities/useApiService";
import { useTemporaryNdcStyles } from "./TemporaryNdcStyles";
import { DialogButtonsWrapper } from "../../pages/adminGenerateCheck/AdminGeneratePageStyles";
import { TEMP_NDCS_QUERY_KEY } from "../../utilities/ndc/temporaryNdcUtils";
import {
  clampNdcInputElevenDigits,
  countNdcDigits,
  isNdcValid,
} from "../../utilities/ndc/ndcUtils";
import { toastFail } from "../../utilities/toast";
import { getApiErrorMessage } from "../../utilities/apiErrors";
import {
  useMedispanDrugFormOptions,
  withCurrentSelectOption,
} from "../../hooks/useMedispanDrugFormOptions";
import {
  CONTROL_SUBSTANCE_CODE_OPTIONS,
  formatControlSubstanceCodeLabel,
} from "../../utilities/ndc/controlSubstanceCode";
import { TemporaryDrugFormSelect } from "./TemporaryDrugFormSelect";
import { useManufacturers } from "../../hooks/useManufacturers";
import { loadNextPageIfAvailable } from "../../hooks/infiniteListTypes";

interface Props {
  open: boolean;
  onClose: () => void;
  drug: any;
}

export const EditTemporaryNdcModal: React.FC<Props> = ({
  open,
  onClose,
  drug,
}) => {
  const { patchMethod } = useApiService();
  const queryClient = useQueryClient();

  const { register, handleSubmit, reset, control, watch } = useForm();
  const classes = useTemporaryNdcStyles();
  const {
    dosageOptions,
    packageUomOptions,
    strengthUomOptions,
    isLoading: formOptionsLoading,
  } = useMedispanDrugFormOptions(open);

  const strengthUom = watch("strengthUom");
  const dosageForm = watch("dosageForm");
  const packageUom = watch("packageUom");
  const controlledSubstanceCode = watch("controlledSubstanceCode");

  const strengthUomSelectOptions = withCurrentSelectOption(
    strengthUomOptions,
    strengthUom,
  );
  const dosageSelectOptions = withCurrentSelectOption(
    dosageOptions,
    dosageForm,
  );
  const packageUomSelectOptions = withCurrentSelectOption(
    packageUomOptions,
    packageUom,
  );
  const cscSelectOptions = withCurrentSelectOption(
    [...CONTROL_SUBSTANCE_CODE_OPTIONS],
    controlledSubstanceCode,
  );

  const [manufacturerSearch, setManufacturerSearch] = useState("");
  const [manufacturers, , manufacturerState] = useManufacturers(
    manufacturerSearch,
    undefined,
    { enabled: open, debounceMs: manufacturerSearch ? 250 : 0 },
  );

  useEffect(() => {
    if (drug) {
      reset({
        ...drug,
        ndc:
          drug.ndc != null
            ? clampNdcInputElevenDigits(String(drug.ndc))
            : drug.ndc,
      });
      setManufacturerSearch(drug.manufacturer ?? "");
    }
  }, [drug, reset]);

  const updateApi = async (data: any) => {
    const res = await patchMethod(`/temporary-ndcs/${drug.id}`, data);
    const errorMessage = getApiErrorMessage(
      res,
      "Failed to update temporary NDC.",
    );
    if (errorMessage) {
      throw new Error(errorMessage);
    }
    return res.data;
  };

  const { mutate: updateDrug, isLoading } = useMutation(updateApi, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(TEMP_NDCS_QUERY_KEY);
      onClose();
    },
    onError: (error: Error) => {
      toastFail(error.message || "Failed to update temporary NDC.");
    },
  });

  const onSubmit = (data: any) => {
    const ndc = clampNdcInputElevenDigits(String(data.ndc ?? ""));
    updateDrug({ ...data, ndc });
  };

  return (
    <Dialog open={open} fullWidth maxWidth="md" onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent className={classes.dialogContent}>
          <TextBox
            headerFontSize={24}
            bodyFontSize={16}
            title="Edit Temporary NDC"
            paragraphs={[""]}
            image=""
          />
          {/* PRODUCT */}
          <h3 className={classes.sectionTitle}>Product</h3>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Controller
                name="ndc"
                control={control}
                rules={{
                  required: "NDC is required",
                  validate: (v) => {
                    if (countNdcDigits(v) > 11) {
                      return "NDC cannot exceed 11 digits (hyphens and spaces are ignored)";
                    }
                    if (!isNdcValid(String(v ?? ""))) {
                      return "NDC must be 9 to 11 digits";
                    }
                    return true;
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    label="NDC"
                    fullWidth
                    required
                    error={!!fieldState.error}
                    helperText={
                      fieldState.error?.message ??
                      "9-11 digits; dashes optional"
                    }
                    {...field}
                    onChange={(e) => {
                      field.onChange(
                        clampNdcInputElevenDigits(e.target.value),
                      );
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Description"
                fullWidth
                {...register("drugName")}
              />
            </Grid>
          </Grid>

          {/* PRODUCT DETAILS */}
          <h3 className={classes.sectionTitle}>Product Details</h3>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <TextField label="Strength" fullWidth {...register("strength")} />
            </Grid>

            <Grid item xs={3}>
              <TemporaryDrugFormSelect
                name="strengthUom"
                label="Strength UOM"
                control={control}
                options={strengthUomSelectOptions}
                loading={formOptionsLoading}
              />
            </Grid>

            <Grid item xs={3}>
              <TemporaryDrugFormSelect
                name="dosageForm"
                label="Dosage"
                control={control}
                options={dosageSelectOptions}
                loading={formOptionsLoading}
              />
            </Grid>

            <Grid item xs={3}>
              <TemporaryDrugFormSelect
                name="manufacturer"
                label="Manufacturer"
                control={control}
                options={manufacturers ?? []}
                freeSolo
                onSearchChange={setManufacturerSearch}
                onListOpen={() => {
                  if (!manufacturers?.length) {
                    void manufacturerState.refetch();
                  }
                }}
                onLoadMore={() => loadNextPageIfAvailable(manufacturerState)}
                loading={
                  manufacturerState.isFetching &&
                  !manufacturerState.isFetchingNextPage
                }
                loadingMore={manufacturerState.isFetchingNextPage}
              />
            </Grid>

            <Grid item xs={3}>
              <TemporaryDrugFormSelect
                name="controlledSubstanceCode"
                label="Control Substance Code"
                control={control}
                options={cscSelectOptions}
                formatLabel={formatControlSubstanceCodeLabel}
                loading={formOptionsLoading}
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                label="Package Size"
                fullWidth
                {...register("packageSize")}
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                label="Package Quantity"
                fullWidth
                {...register("packageQuantity")}
              />
            </Grid>

            <Grid item xs={3}>
              <TemporaryDrugFormSelect
                name="packageUom"
                label="Package UOM"
                control={control}
                options={packageUomSelectOptions}
                loading={formOptionsLoading}
              />
            </Grid>
          </Grid>

          {/* PRICING */}
          <h3 className={classes.sectionTitle}>Pricing</h3>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <TextField
                label="WAC Package Price"
                fullWidth
                {...register("packagePrice")}
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                label="WAC Unit Price"
                fullWidth
                {...register("unitPrice")}
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                label="Estimated Package Price"
                fullWidth
                {...register("packageActualPrice")}
              />
            </Grid>

            <Grid item xs={3}>
              <TextField
                label="Estimated Unit Price"
                fullWidth
                {...register("unitActualPrice")}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <DialogButtonsWrapper>
            <BigRedButton
              text="CANCEL"
              version={ButtonVersion.SMALL}
              onClick={onClose}
            />

            <Button
              className="approveButton"
              type="submit"
              disabled={isLoading}
            >
              Save
            </Button>
          </DialogButtonsWrapper>
        </DialogActions>
      </form>
    </Dialog>
  );
};
